import * as uuid from 'uuid';
import { formatDate } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { iif, Observable, of, throwError, zip } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { RequestEvent, RequestRoot } from 'types';
import { InfoSiteRequest } from '../model/info-site-request';
import { SubscriptionCreationData } from '../model/subscription-creation-data';
import { SubscriptionCreationResult } from '../model/subscription-creation-result';
import { StatusChangeNotification } from '../model/application-notification';
import { SubscriptionUpdateResult } from '../model/subscription-update-result';
import { InfoSiteService } from './info-site.service';
import { NotificationService } from './notification-service';
import { RequestFeatures } from 'src/webid/4.0/model/features';
import { RequestFeaturesV2 } from 'src/webid/4.0/model/features2';
import { RequestDataAccessService } from 'src/webid/4.0/projects/data-access/src/lib/request-data-access.service';

@Injectable()
export class SubscriptionService {
    constructor(
        private infoSiteService: InfoSiteService,
        private requestService: RequestDataAccessService,
        private notificationService: NotificationService,
        @Inject(LOCALE_ID) private locale: string
    ) {}

    subscribeNewRequest(
        data: SubscriptionCreationData
    ): Observable<SubscriptionCreationResult | null> {
        if (data.departmentCode && data.sequence) {
            return this.searchRequestOnServer(
                data.departmentCode,
                data.sequence
            ).pipe(
                concatMap((info) =>
                    this.verifyFoundRequest(info, data.birthDate).pipe(
                        map(() => info)
                    )
                ),
                concatMap((info) =>
                    zip(this.createRequest(info?.uid ?? '', data), of(info))
                ),
                concatMap(([request, info]) =>
                    this.applyRequestStatus(
                        request,
                        info?.actual() ?? null,
                        true
                    ).pipe(map(() => request))
                ),
                concatMap((request) =>
                    this.requestService.save(request).pipe(map(() => void 0))
                ),
                concatMap(() => of(SubscriptionCreationResult.success())),
                catchError((error) =>
                    of(SubscriptionCreationResult.failure(error))
                )
            );
        } else {
            return of(null);
        }
    }

    subscribeExistingRequest(
        requestId: string,
        department: string,
        sequence: string
    ): Observable<SubscriptionCreationResult> {
        const requestRootBuffer = new RequestRoot();
        requestRootBuffer.id = requestId;

        return this.requestService.load(requestRootBuffer).pipe(
            concatMap((request) =>
                zip(
                    this.searchRequestOnServer(department, sequence),
                    of(request)
                )
            ),
            concatMap(([info, request]) =>
                zip(
                    this.verifyFoundRequest(
                        info,
                        this.getApplicantBirthDate(request)
                    ).pipe(map(() => info)),
                    this.applyRequestStatus(
                        request,
                        info?.actual() ?? null,
                        true
                    ).pipe(map(() => request))
                )
            ),
            concatMap(([info, request]) => {
                request.uid = info?.uid ?? undefined;
                return this.requestService
                    .edit(request)
                    .pipe(map(() => void 0));
            }),
            concatMap(() => of(SubscriptionCreationResult.success())),
            catchError((error) => of(SubscriptionCreationResult.failure(error)))
        );
    }

    subscribeExistingRequestByUid(
        requestId: string,
        requestUid: string
    ): Observable<SubscriptionCreationResult> {
        return this.subscribeExistingRequest(requestId, '', requestUid);
    }

    updateSubscriptionStatus(
        requestId: string,
        markAsRead: boolean
    ): Observable<SubscriptionUpdateResult | null> {
        const requestRootBuffer = new RequestRoot();
        requestRootBuffer.id = requestId;

        return this.requestService.load(requestRootBuffer).pipe(
            concatMap((request) =>
                zip(
                    this.searchRequestOnServer('', request?.uid ?? '').pipe(
                        concatMap((info) =>
                            iif(
                                () => info === null,
                                throwError(
                                    () => new Error('REQUEST_NOT_FOUND')
                                ),
                                of(info)
                            )
                        )
                    ),
                    of(request)
                )
            ),
            concatMap(([info, request]) =>
                zip(
                    of(info),
                    this.applyRequestStatus(
                        request,
                        info?.actual() ?? null,
                        markAsRead
                    ),
                    this.requestService.edit(request).pipe(map(() => request)),
                )
            ),
            map(([info, status, request ]) =>
                info
                    ? new SubscriptionUpdateResult(request, info, status)
                    : null
            )
        );
    }

    private applyRequestStatus(
        request: RequestRoot,
        info: InfoSiteRequest | null,
        markAsRead: boolean
    ): Observable<boolean> {
        if (request.status === info?.passportStatus.code) {
            return of(false);
        }
        let nextStatusEvent = this.createRequestEvent(
            info?.passportStatus.code ?? ''
        );
        let skipNotification =
            request.status === 'DATA_ENTERED' ||
            request.status === 'READY' ||
            request.status === '';

        return this.addRequesEvent(request, info, nextStatusEvent).pipe(
            concatMap(() => {
                if (skipNotification) {
                    return of(false);
                }
                let notification = this.createNotification(
                    request,
                    nextStatusEvent,
                    markAsRead
                );
                return this.notificationService
                    .create(notification)
                    .pipe(map(() => true));
            })
        );
    }

    private searchRequestOnServer(
        department: string,
        sequence: string
    ): Observable<InfoSiteRequest | null> {
        return iif(
            () => sequence.length === 25,
            this.infoSiteService.findByRequestUid(sequence),
            this.infoSiteService.findByDepartmentAndSequence(
                department,
                sequence
            )
        );
    }

    private verifyFoundRequest(
        info: InfoSiteRequest | null,
        birthDate: Date | null
    ): Observable<void> {
        return iif(
            () => info === null,
            throwError(() => new Error('REQUEST_NOT_FOUND')),
            of(info).pipe(
                concatMap((info) =>
                    this.isStatusPending(info!)
                        ? throwError(() => new Error('REQUEST_NOT_ACCEPTED'))
                        : of(info)
                ),
                concatMap((info) =>
                    this.infoSiteService.verify(info?.uid ?? '', birthDate)
                )
            )
        );
    }

    private createRequest(
        uid: string,
        data: SubscriptionCreationData
    ): Observable<RequestRoot> {
        let request = RequestRoot.init(uid, RequestFeaturesV2);
        let features: RequestFeaturesV2 = request.features;

        features.applicant.personIdentity.fullName.lastName =
            data.lastName ?? '';
        features.applicant.personIdentity.fullName.firstName =
            data.firstName ?? '';
        features.applicant.personIdentity.fullName.middleName.value =
            data.middleName;

        features.applicant.personIdentity.birthDate.value = formatDate(
            data.birthDate ?? '',
            'yyyy-MM-dd',
            this.locale
        );

        features.mobileOptions.department = data.departmentCode ?? '';
        return of(request);
    }

    private addRequesEvent(
        request: RequestRoot,
        info: InfoSiteRequest | null,
        event: RequestEvent
    ): Observable<void> {
        return new Observable<void>((observer) => {
            request.status = info?.passportStatus.code ?? undefined;
            request.eventHistory?.push(event);
            observer.next(void 0);
            observer.complete();
        });
    }

    private createRequestEvent(type: string): RequestEvent {
        let evt = new RequestEvent();
        evt.created = new Date();
        evt.processed = evt.created;
        evt.type = type;
        evt.user = '';
        evt.location = '';
        evt.comment = '';
        evt.stateChangeSystem = '';
        evt.id = '';
        evt.correlationId = '';
        evt.tag = '';
        return evt;
    }

    private createNotification(
        request: RequestRoot,
        nextStatusEvent: RequestEvent,
        markAsRead: boolean
    ): StatusChangeNotification {
        let notification = new StatusChangeNotification();
        notification.id = uuid.v4().toString();
        notification.requestId = request.id;
        notification.requestUid = request.uid;
        notification.created = nextStatusEvent.created;
        notification.previousStatus = request.status;
        notification.nextStatus = nextStatusEvent.type;
        notification.read = markAsRead;
        return notification;
    }

    private getApplicantBirthDate(request: RequestRoot): Date | null {
        let features: RequestFeatures | RequestFeaturesV2 = request.features;
        return features.applicant.personIdentity.birthDate.toUTCDate();
    }

    private isStatusPending(info: InfoSiteRequest): boolean {
        return (
            info.actual().passportStatus.code === 'INFO_PENDING_DATA_PROCESSING'
        );
    }
}
